import React, { useContext, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDebouncedCallback } from "use-debounce"
import api from "../../../api2"
import { SiteProps } from "../../../interfaces"
import { AdminContext } from "~/contexts/AdminContext"
import { Option } from "../../ui/SelectInput"
import IAddOn, { ADDON_KIND } from "../../../interfaces/IAddOn"
import { Combobox } from "@headlessui/react"
import { ChevronUpDownIcon } from "@heroicons/react/24/outline"
import classNames from "../../../utils/classNames"
import ILineItem from "../../../interfaces/ILineItem"
import Modal from "~/components/ui/Modal"

const mapProductToOption = (product: IAddOn): Option => ({
  label: product.label,
  value: product.id,
})

interface ComponentProps {
  site: SiteProps
  disabledProductIds?: string[]
  onChange: (product: IAddOn) => void
  createNewItem: (name: string, id?: string) => void
  selectedId?: string
  lineItem: Partial<ILineItem>
  campingStyleId?: string
}

const ProductDropdown = ({
  site,
  onChange,
  disabledProductIds,
  createNewItem,
  selectedId,
  lineItem,
}: ComponentProps) => {
  const { camp } = useContext(AdminContext)
  const [isOpen, setIsOpen] = useState(false)
  const [products, setProducts] = useState<IAddOn[]>([])
  const [filteredProducts, setFilteredProducts] = useState<IAddOn[]>([])
  const [selectedOption, setSelectedOption] = useState<string>(selectedId)
  const [inputValue, setInputValue] = useState(lineItem.label)
  const [isEnterPressed, setIsEnterPressed] = useState(false)
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const comboBoxRef = useRef<HTMLInputElement | null>(null)
  
  useEffect(() => {
    fetchProducts()
  }, [])

  useEffect(() => {
    setInputValue(lineItem.label)
  }, [lineItem])

  useEffect(() => {
    if (selectedId) setSelectedOption(selectedId)
  }, [selectedId])

  const changeSelectedOption = (newOptionId: string) => {
    if (newOptionId) {
      const product = products.find((p) => p.id === newOptionId)
      if (product) {
        onChange(product)
      } else {
        setSelectedOption("")
        createNewItem(newOptionId, lineItem.add_on_id)
      }
    }
    setSelectedOption(newOptionId)
  }

  const fetchProducts = useDebouncedCallback(async () => {
    api.Products.forCamp(camp.id, { siteIds: [site.id] })
      .then(({ products: p }) => {
        const filteredProducts = p.filter((product: IAddOn) =>
          [ADDON_KIND.NORMAL, ADDON_KIND.BOOLEAN].includes(product.kind),
        )
        setProducts(filteredProducts)
      })
      .catch((err) => {
        console.error(err)
      })
  }, 100)

  const filterProducts = (query: string) => {
    if (!query) {
      setFilteredProducts(products)
    } else {
      setFilteredProducts(products.filter((p) => p.label.toLowerCase().includes(query.toLowerCase())))
    }
  }

  const onSelect = (value: string) => {
    changeSelectedOption(value)
    setIsOpen(false)
    setInputValue(products.find((p) => p.id === value)?.label || value || "")
  }
  
  const getDropdownStyle = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      return {
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      }
    }
    return {}
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        comboBoxRef.current &&
        !comboBoxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const closeModal = () => {
      setIsOpen(false)
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("touchstart", handleClickOutside)
    document.querySelector(".admin-scrollable-block")?.addEventListener("scroll", closeModal)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("touchstart", handleClickOutside)
      document.querySelector(".admin-scrollable-block")?.removeEventListener("scroll", closeModal)
    }
  }, [])
  
  return (
    <>
      <Combobox
        as="div"
        value={selectedOption}
        disabled={lineItem?.is_always_applied}
        onChange={onSelect}
      >
        {({ open }) => (
          <>
            <div className="relative" ref={triggerRef}>
              <Combobox.Button
                className="w-full ghostinspector-new-reservation-site-button"
                disabled={lineItem?.is_always_applied}
              >
                <Combobox.Input
                  autoComplete="off"
                  className={classNames(
                    "w-full rounded-md border border-gray-300 bg-white pl-3 pr-12 text-gray-900 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:leading-6 h-10.5 focus:border-transparent",
                    lineItem?.is_always_applied && "bg-gray-100 cursor-not-allowed",
                  )}
                  disabled={lineItem?.is_always_applied}
                  onChange={(e) => {
                    const value = e.target.value
                    setInputValue(value)
                    filterProducts(value)
                    setIsEnterPressed(false)
                  }}
                  value={inputValue}
                  displayValue={(id: string) =>
                    lineItem?.add_on_id?.length && lineItem?.add_on_id !== "change-me"
                      ? (lineItem.label as string)
                      : (inputValue as string)
                  }
                  onClick={() => {
                    filterProducts("")
                    setIsEnterPressed(false)
                  }}
                  onFocus={() => {
                    setIsEnterPressed(false)
                    setIsOpen(true)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !filteredProducts.length) {
                      createNewItem(inputValue, lineItem.add_on_id)
                      setIsEnterPressed(true)
                      e.preventDefault()
                    }
                  }}
                  onBlur={() => {
                    if (inputValue.trim() && !filteredProducts.length && !isEnterPressed) {
                      createNewItem(inputValue, lineItem.add_on_id)
                    }
                  }}
                  ref={inputRef}
                />
                <div className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
              </Combobox.Button>
              {filteredProducts.length > 0 &&
                open && isOpen &&
                !isEnterPressed &&
                createPortal(
                  <Combobox.Options
                    ref={comboBoxRef}
                    static
                    style={getDropdownStyle()}
                    className={classNames(
                      "max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm",
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {inputValue.length > 0 &&
                    (!selectedOption?.length || inputValue !== products.find((p) => p.id === selectedOption)?.label) ? (
                      <Combobox.Option
                        value={inputValue}
                        onClick={() => setIsOpen(false)}
                        className={({ active, selected }) =>
                          classNames(
                            "relative select-none py-2 px-3 ghostinspector-new-reservation-site-li",
                            disabledProductIds?.includes(inputValue) ? "opacity-50" : "cursor-pointer",
                            selected ? "bg-green-50" : active ? "bg-green-600 text-white" : "text-gray-900",
                          )
                        }
                      >
                        {inputValue}
                      </Combobox.Option>
                    ) : null}
                    {filteredProducts.map(mapProductToOption).map((option, index) => (
                      <Combobox.Option
                        key={index}
                        onClick={() => setIsOpen(false)}
                        value={option.value}
                        disabled={disabledProductIds?.includes(option.value)}
                        className={({ active, selected }) =>
                          classNames(
                            "relative select-none py-2 px-3 ghostinspector-new-reservation-site-li",
                            disabledProductIds?.includes(option.value) ? "opacity-50" : "cursor-pointer",
                            selected ? "bg-green-50" : active ? "bg-green-600 text-white" : "text-gray-900",
                          )
                        }
                      >
                        {({ active, selected }) => (
                          <div className="flex justify-between">
                            <div className="flex flex-col">
                              <span className={classNames("grow truncate text-md", selected && "font-semibold")}>
                                {option.label}
                              </span>
                            </div>
                          </div>
                        )}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>,
                  document.body,
                )}
            </div>
          </>
        )}
      </Combobox>
    </>
  )
}

export default ProductDropdown
